/**
 * @file FeaturedHeroRail.tsx
 * @description Featured content carousel with auto-advance and navigation
 *
 * Features:
 * - Fetches featured collection from API
 * - Horizontal scroll carousel with snap points
 * - Auto-advance every 8 seconds
 * - Manual navigation via arrow buttons and indicators
 * - Pause auto-advance on hover
 * - Loading and error states
 *
 * @architecture
 * Implements RAILS-02 Featured Content Rail
 * Uses ARCH-001 collections-based content strategy
 */
/* eslint-disable max-lines-per-function */

import type { ApiTitle } from "@lumi/data";
import type { Variants } from "framer-motion";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { apiCall } from "../../../config/api";
import { CarouselNavigation } from "../../../shared/components/CarouselNavigation";
import { HeroCard } from "../../../shared/components/content-cards/HeroCard";
import { TitleCard } from "../../../shared/components/content-cards/TitleCard";
import { useRailNavigation } from "../../../shared/hooks/useRailNavigation";
import { pageTransitions } from "../../../shared/transitions/pageTransitions";

const AUTO_ADVANCE_INTERVAL = 8000; // 8 seconds

/**
 * Fetches featured collection from API
 */
async function loadFeaturedCollection(): Promise<ApiTitle[]> {
  try {
    const response = await apiCall("/v1/collections/featured");
    if (!response.ok) {
      throw new Error(`Failed to load featured content: ${response.status}`);
    }

    interface CollectionResponse {
      items: Array<{ rank: number; title: ApiTitle }>;
    }

    const data = (await response.json()) as CollectionResponse;
    return data.items.map((item) => item.title);
  } catch (err) {
    console.error("❌ Failed to load featured collection:", err);
    return [];
  }
}

/**
 * Featured hero carousel component
 *
 * Displays featured content in landscape hero cards with auto-advance
 * Replaces static HeroBillboard with dynamic collection-based content
 * Uses keyboard navigation hook for focus management and smooth scrolling
 */

export function FeaturedHeroRail({
  transitionTo,
}: {
  readonly transitionTo: (path: string, variant?: Variants) => void;
}): ReactElement {
  const [items, setItems] = useState<ApiTitle[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoAdvanceRef = useRef<NodeJS.Timeout | null>(null);

  // Keyboard navigation for cards - this is the single source of truth for which card is focused
  const { focusedIndex, railRef, handleRailFocus, handleRailBlur, handleRailKeyDown, getCardProps, navigateToIndex } =
    useRailNavigation({
      itemCount: items.length,
      initialIndex: 3,
      disableAutoScroll: true, // Carousel handles its own horizontal scrolling
      onAction: (index) => {
        const title = items[index];
        if (title) {
          transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade);
        }
      },
      onInfo: (index) => {
        const title = items[index];
        if (title) {
          transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
        }
      },
    });

  // Wrap rail focus/blur handlers to pause auto-advance
  const handleFocus = () => {
    setIsPaused(true); // Pause auto-advance when navigating via keyboard
    handleRailFocus();
  };

  const handleBlur = () => {
    setIsPaused(false); // Resume auto-advance when leaving rail
    handleRailBlur();
  };

  // Fetch featured collection
  useEffect(() => {
    loadFeaturedCollection()
      .then((titles) => {
        setItems(titles);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading featured collection:", err);
        setLoading(false);
      });
  }, []);

  // Scroll carousel to focused card (horizontal scroll within carousel container)
  useEffect(() => {
    if (!scrollRef.current || items.length === 0) return;

    const container = scrollRef.current;
    const cards = container.querySelectorAll("[data-card]");
    const targetCard = cards[focusedIndex] as HTMLElement;

    if (targetCard) {
      // Calculate centered scroll position manually
      const containerWidth = container.offsetWidth;
      const cardLeft = targetCard.offsetLeft;
      const cardWidth = targetCard.offsetWidth;

      // Center the card: scroll so card center aligns with container center
      const scrollPosition = cardLeft - containerWidth / 2 + cardWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [focusedIndex, items.length]);

  // Center first card on initial mount - REMOVED: CSS padding handles this now
  // useEffect(() => {
  //     if (!scrollRef.current || items.length === 0 || focusedIndex !== 0) return;
  //     ...
  // }, [items.length, focusedIndex]);

  // Auto-advance carousel - wraps around to first item
  useEffect(() => {
    if (items.length <= 1 || isPaused) return;

    autoAdvanceRef.current = setInterval(() => {
      navigateToIndex((focusedIndex + 1) % items.length);
    }, AUTO_ADVANCE_INTERVAL);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
      }
    };
  }, [items.length, isPaused, focusedIndex, navigateToIndex]);

  const handlePrevious = () => {
    // Wrap to last item if at first
    const newIndex = focusedIndex === 0 ? items.length - 1 : focusedIndex - 1;
    navigateToIndex(newIndex);
  };

  const handleNext = () => {
    // Wrap to first item if at last
    const newIndex = (focusedIndex + 1) % items.length;
    navigateToIndex(newIndex);
  };

  // Show loading skeleton to reserve vertical space
  if (loading) {
    return (
      <section aria-label="Featured content loading" className="relative py-20 -my-12 flex items-center justify-center">
        <div className="flex gap-8 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide w-full py-12">
          {[1, 2, 3].map((i) => (
            <div key={i} className="snap-center flex items-center justify-center">
              {/* Desktop: HeroCard skeleton */}
              <div className="hidden sm:block shadow-[0_10px_40px_rgba(0,0,0,0.7)] rounded-lg">
                <div className="w-[92vw] md:w-[70vw] lg:w-[68vw] xl:w-[55vw] aspect-video bg-neutral-900 rounded-lg animate-pulse" />
              </div>
              {/* Mobile: TitleCard skeleton */}
              <div className="sm:hidden shadow-[0_10px_40px_rgba(0,0,0,0.7)]">
                <div className="w-[calc(100vw-4rem)] aspect-[2/3] bg-neutral-900 rounded-lg animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Don't render if no items
  if (items.length === 0) {
    return <></>;
  }

  // Prevent rail from receiving focus on click - only allow keyboard focus
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
  };

  return (
    <section aria-label="Featured content carousel" className="relative">
      {/* Roving tabindex pattern: ul[tabIndex={0}] enables keyboard focus on the rail container.
                This is intentional and WCAG-compliant for carousel/rail navigation.
                See: https://www.w3.org/WAI/ARIA/apg/patterns/carousel/ */}
      {}
      <ul //NOSONAR
        ref={railRef as React.RefObject<HTMLUListElement>}
        data-rail
        tabIndex={0} //NOSONAR
        className="py-20 -my-12 flex items-center justify-center focus:outline-none"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleRailKeyDown}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onPointerDown={handlePointerDown}
      >
        {/* Carousel container with padding to center items */}
        <div
          ref={scrollRef}
          className="flex gap-8 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide w-full py-16 px-[4vw] sm:px-[4vw] md:px-[15vw] lg:px-[16vw] xl:px-[22.5vw]"
          style={{
            scrollSnapType: "x mandatory",
          }}
        >
          {items.map((title, index) => {
            const cardProps = getCardProps(index);

            return (
              <li
                key={title.id}
                data-rail-item
                data-card
                className="snap-center flex items-center justify-center"
                ref={cardProps.ref}
              >
                {/* Desktop: HeroCard with landscape layout */}
                <div className="hidden sm:block">
                  <HeroCard title={title} transitionTo={transitionTo} className={cardProps.className} />
                </div>
                {/* Mobile: TitleCard with portrait layout */}
                <div className="sm:hidden">
                  <TitleCard title={title} transitionTo={transitionTo} className={cardProps.className} />
                </div>
              </li>
            );
          })}
        </div>

        {/* Edge gradients to obscure empty padding space */}
        <div className="absolute left-0 top-0 bottom-0 w-[4vw] sm:w-[4vw] md:w-[15vw] lg:w-[16vw] xl:w-[22.5vw] bg-gradient-to-r from-black to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-[4vw] sm:w-[4vw] md:w-[15vw] lg:w-[16vw] xl:w-[22.5vw] bg-gradient-to-l from-black to-transparent pointer-events-none" />
      </ul>

      {/* Navigation arrows - outside rail to prevent triggering keyboard nav mode */}
      {items.length > 1 && <CarouselNavigation onPrevious={handlePrevious} onNext={handleNext} />}
    </section>
  );
}
